import { inject, Injectable, signal } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { SsApiService } from '../api/ss-api.service';
import { catchError, finalize, first, map, share, switchMap, tap, timeout } from 'rxjs/operators';
import { UserInfoService } from './user-info.service';
import { CmsApiService } from '../api/cms-api.service';
import { CookieService, PlatformService } from 'ngx-unificator/services';
import { Router } from '@angular/router';
import { LocalstorageService } from '../localstorage.service';
import { UserPaymentV2Service } from './user-payment-v2.service';
import { LocalHistoryService } from '../local-history.service';
import { CurrencyService, NOT_NEEDED_CURRENCIES } from '../currency.service';
import { CountryService } from '../country.service';
import { RestrictSignupService } from '../restrict-signup.service';

export enum DEPOSIT_MODAL_LIMITS {
  EUR = 1,
  USD = 1,
  NZD = 2,
  NOK = 10,
  AUD = 1,
  CAD = 1,
  PHP = 60,
  BRL = 5,
  ZAR = 20,
  JPY = 160,
  PLN = 4,
  KRW = 1400,
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _ssApi = inject(SsApiService);
  private _cmsApi = inject(CmsApiService);
  private _currency = inject(CurrencyService);
  private _userInfo = inject(UserInfoService);
  private _cookies = inject(CookieService);
  private _router = inject(Router);
  private _localStorage = inject(LocalstorageService);
  private _payment = inject(UserPaymentV2Service);
  private _local = inject(LocalHistoryService);
  private _country = inject(CountryService);
  private _platform = inject(PlatformService);
  private _restrictSignup = inject(RestrictSignupService);

  private _auth$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private _accountWalletList$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _accountList$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _currentCurrency$: ReplaySubject<string> = new ReplaySubject<string>(1);

  private _auth: boolean;
  private _playerStats: any = {};
  private _info: any = {};
  private _currentCurrency: any = {};
  private _accountList: Array<any> = [];
  private _accountWalletList: any[] = [];

  public isUserAlreadyRegistered = signal<boolean>(this._cookies.check('registered')).asReadonly();

  constructor() {
    /**
     * For avoiding circular dependency
     */
    this._userInfo.userService = this;
    this._payment.userService = this;
  }

  /**
   * Access to private fields from outside
   */
  get auth(): boolean {
    return this._auth;
  }

  get auth$(): ReplaySubject<boolean> {
    return this._auth$;
  }

  get info(): any {
    return this._info;
  }

  get currentCurrency(): any {
    return this._currentCurrency;
  }

  get currency$(): ReplaySubject<string> {
    return this._currentCurrency$;
  }

  get accountList(): Array<any> {
    return this._accountList;
  }

  get accountList$(): ReplaySubject<any> {
    return this._accountList$;
  }

  get accountWalletList(): Array<any> {
    return this._accountWalletList;
  }

  get accountWalletList$(): ReplaySubject<any> {
    return this._accountWalletList$;
  }

  get stats(): any {
    return this._playerStats;
  }

  get hasDeposit(): boolean {
    return !!this._playerStats.deposits_count;
  }

  get currentCurrencyToEquivalent(): any {
    return DEPOSIT_MODAL_LIMITS[this.currentCurrency?.currency];
  }

  /**
   * Get user account list and save to local var
   */
  getUserAccountsWallet(): Observable<any> {
    return this._requestPlayerAccounts().pipe(
      tap(accountList => {
        this._currency.currencyList$.pipe(first()).subscribe(() => {
          const serialized = this._serializeCurrencyValues(accountList);
          this._accountWalletList = serialized;
          this._accountWalletList$.next(serialized);
        })
      }),
    );
  }

  /**
   * Get all user available account list and save to local var
   */
  getUserAccounts(): Observable<any> {
    return this._requestPlayerAccounts({ compatibility: false }).pipe(
      tap(accountList => {
        this._currency.currencyList$.pipe(first()).subscribe(() => {
          const serialized = this._serializeCurrencyValues(accountList);
          this._accountList = serialized;
          this._accountList$.next(serialized);
          this.updateCurrentCurrencyBalance();
        });
      }),
    );
  }

  /**
   * Get user info and save to local var
   */
  getUserInfo(): Observable<any> {
    if (!this._platform.isBrowser) {
      this._auth = false;
      this.auth$.next(false);
      return of({});
    }
    let timeoutOccurred = false;

    return this._ssApi.player().pipe(
      timeout(2000),
      tap({
        next: () => {
          timeoutOccurred = false;
        },
        error: () => {
          timeoutOccurred = true;
          const defaultInfo = { language: 'en' };
          this.applyUserInfo(defaultInfo);
        },
      }),
      catchError(() => {
        if (timeoutOccurred) {
          return this._ssApi.player();
        } else {
          return of({});
        }
      }),
      tap(info => {
        this.applyUserInfo(info);
      }),
      finalize(() => {
        if (timeoutOccurred) {
          this._router.navigateByUrl(this._local.getUrl('before-login')?.url);
        }
      }),
    );
  }

  /**
   * Get additional player`s data from backend
   */
  getPlayerStats(): Observable<any> {
    return this._ssApi.playerStats().pipe(
      catchError(() => of({})),
      tap(stats => {
        this._playerStats = stats;
      }),
    );
  }

  updateAllAccounts() {
    return combineLatest([this.getUserAccounts(), this.getUserAccountsWallet()]);
  }

  /**
   * Save user info to local var and declare user state
   *
   * @param info
   */
  applyUserInfo(info: any) {
    this._info = {
      ...info,
      gender_name: info.gender === 'm' ? 'Male' : info.gender === 'f' ? 'Female' : '',
      country_name: this._country.countryName(info.country || ''),
    };

    if ('id' in info) {
      this._localStorage.set('user_id', `${info.id}`);
      this._cmsApi.customHeadersList.push({ key: 'UID', val: `${info.id}` });
      this._cmsApi.customNoLangHeadersList.push({ key: 'UID', val: `${info.id}` });
      if (info.country) {
        this._cmsApi.customHeadersList.push({ key: 'UC', val: info.country });
        this._cmsApi.customNoLangHeadersList.push({ key: 'UC', val: info.country });
      }

      if (info.statuses && info.statuses.length) {
        const statusesHeaders = {
          key: 'U-GROUP',
          val: info.statuses.map(status => encodeURI(status.id)).join(','),
        };
        this._cmsApi.customHeadersList.push(statusesHeaders);
        this._cmsApi.customNoLangHeadersList.push(statusesHeaders);
      }
      this._auth = true;
      this.auth$.next(true);
      this._cookies.set('registered', '1', 999, '/', window.location.hostname as any);
      this._userInfo.checkMissingAuthFields(info);
    } else {
      this._auth = false;
      this.auth$.next(false);
    }
  }

  private _requestPlayerAccounts(params: any = {}): Observable<any[]> {
    return this._ssApi.playerAccounts(params).pipe(
      map(accountList => accountList.filter((account: any) => !NOT_NEEDED_CURRENCIES.includes(account.currency))),
      catchError(() => of([])),
    );
  }

  private _serializeCurrencyValues(accountList: any[]) {
    return accountList.map(account => {
      return {
        ...account,
        amount: this._currency.subunitsToUnits(account.amount_cents, account.currency),
        cashout: this._currency.subunitsToUnits(account.available_to_cashout_cents, account.currency),
        symbol: this._currency.currencySymbol(account.currency),
        isCrypto: this._currency.cryptoCurrencyList().map(e => e.code).includes(account.currency),
        isPopular: account.currency === 'USD' || account.currency === 'BTC',
      };
    });
  }

  /**
   * Fetch all required user data
   *
   * @private
   */
  public fetchAllUserData() {
    const observable = this.getUserInfo().pipe(
      first(),
      switchMap(() => this.getPlayerStats()),
      switchMap(() => this.getUserAccountsWallet()),
      switchMap(() => this.getUserAccounts()),
      share(),
    );

    observable.subscribe();

    return observable;
  }

  /**
   * Update current currency balance info
   *
   * @public
   */
  public updateCurrentCurrencyBalance() {
    this._currentCurrency = this._accountList.find(account => account.currency === this._info.currency) || {};
    this._currentCurrency$.next(this._currentCurrency.currency);
  }

  /**
   * Change user currency account
   * @param selectedCurrency
   */
  public changeCurrencyAcc(selectedCurrency: string) {
    if (selectedCurrency) {
      this._payment.paymentFiatMethodsList.next(null);
      this._payment.paymentCryptoMethodsList.next(null);
      this._currentCurrency$.next(null);
      return this._ssApi.postPlayerAccounts({ currency: selectedCurrency }).pipe(switchMap(() => this.fetchAllUserData()));
    }
  }

  /**
   * Check if user already registered and redirect
   */
  public authUser() {
    return !this._restrictSignup.isSignupRestricted() ?
      this._router.navigateByUrl(this._cookies.check('registered') ? '/login' : '/register') :
      this._router.navigateByUrl('/login');
  }

  /**
   * Check if user already registered and return dynamic translate key
   */
  public get authKeyTranslate(): string {
    return !this._restrictSignup.isSignupRestricted() ?
      this.isUserAlreadyRegistered() ? 'btn.signin' : 'btn.signup' :
      'btn.signin';
  }
}
